import React from "react";
import "./Multistate.scss";

type TSelected = string | string[] | undefined;

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
    multiSelect?: boolean;
    multiselect?: boolean; // alias to support previous prop casing
    selected?: TSelected; // controlled selection
    defaultSelected?: TSelected; // initial selection when uncontrolled
    selectedID?: TSelected; // deprecated alias
    onSelectedChange?: (selected: TSelected) => void;
    children?: React.ReactNode;
}

function normalizeSelection(value: TSelected, multiSelect: boolean): string[] {
    if (value === undefined) return [];
    if (Array.isArray(value)) return multiSelect ? value : value.slice(0, 1);
    return [value];
}

function buildNextSelection(current: string[], id: string, multiSelect: boolean): string[] {
    if (multiSelect) {
        return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
    }
    return [id];
}

function selectionForCallback(selection: string[], multiSelect: boolean): TSelected {
    if (multiSelect) return selection;
    return selection[0];
}

export default function Multistate({
    children,
    className,
    multiSelect,
    multiselect,
    selected,
    defaultSelected,
    selectedID,
    onSelectedChange,
    ...props
}: IProps) {
    const classNames = ["userful-multistate noselect", className].filter(Boolean).join(" ");
    const resolvedMultiSelect = multiSelect ?? multiselect ?? false;
    const controlledSelected = selected ?? selectedID;
    const isControlled = controlledSelected !== undefined;

    const [uncontrolledSelection, setUncontrolledSelection] = React.useState<string[]>(() =>
        normalizeSelection(defaultSelected ?? controlledSelected, resolvedMultiSelect),
    );

    const activeSelection = React.useMemo(
        () => normalizeSelection(isControlled ? controlledSelected : uncontrolledSelection, resolvedMultiSelect),
        [controlledSelected, isControlled, resolvedMultiSelect, uncontrolledSelection],
    );

    const renderChildren = React.useMemo(() => {
        return React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return child;

            const childId = (child.props as { id?: string }).id;
            if (!childId) return child;

            const childOnClick = (child.props as { onClick?: React.MouseEventHandler }).onClick;
            const isActive = activeSelection.includes(childId);

            const handleClick: React.MouseEventHandler = (event) => {
                childOnClick?.(event);
                if (event.defaultPrevented) return;

                const nextSelection = buildNextSelection(activeSelection, childId, resolvedMultiSelect);
                if (!isControlled) {
                    setUncontrolledSelection(nextSelection);
                }
                onSelectedChange?.(selectionForCallback(nextSelection, resolvedMultiSelect));
            };

            return React.cloneElement(child as React.ReactElement<any>, {
                active: isActive,
                onClick: handleClick,
            });
        });
    }, [children, activeSelection, isControlled, resolvedMultiSelect, onSelectedChange]);

    return (
        <div className={classNames} {...props}>
            {renderChildren}
        </div>
    );
}
